import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTheme } from "next-themes";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, ShieldAlert, Trash2, X } from "lucide-react";

import { NumerolojiAdminSidebar } from "@/components/admin/NumerolojiAdminSidebar";
import { AdminLoginCard } from "@/components/admin/AdminLoginCard";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { adminFetch, useAdminSession } from "@/hooks/use-admin-session";
import { cn } from "@/lib/utils";

type Review = {
  id: string;
  createdAt: number;
  packageKey: string;
  name: string;
  rating: number;
  message: string;
  approved: boolean;
  approvedAt?: number;
};

type ReviewsResponse = { ok: true; items: Review[] };

type StorageResponse = { ok: true; reviews: { backend: "supabase" | "disk" } };

async function fetchStorage() {
  const res = await adminFetch("/api/admin/numeroloji/storage");
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Durum alınamadı.");
  }
  return json as StorageResponse;
}

async function fetchReviews(params: { approved?: boolean; packageKey?: string }) {
  const url = new URL("/api/admin/numeroloji/reviews", window.location.origin);
  if (typeof params.approved === "boolean") url.searchParams.set("approved", params.approved ? "1" : "0");
  if (params.packageKey?.trim()) url.searchParams.set("packageKey", params.packageKey.trim());

  const res = await adminFetch(url.pathname + url.search);
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Yorumlar alınamadı.");
  }
  // Be defensive: if the API ever responds with a non-array `items`, don't crash the UI.
  return {
    ok: true,
    items: Array.isArray(json?.items) ? (json.items as Review[]) : [],
  };
}

async function setApproved(input: { id: string; approved: boolean }) {
  const res = await adminFetch(`/api/admin/numeroloji/reviews/${encodeURIComponent(input.id)}/approve`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ approved: input.approved }),
  });

  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Güncellenemedi.");
  }
  return json as { ok: true; item: Review };
}

async function deleteReview(id: string) {
  const res = await adminFetch(`/api/admin/numeroloji/reviews/${encodeURIComponent(id)}/delete`, {
    method: "POST",
  });

  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Silinemedi.");
  }
  return json as { ok: true; deleted: boolean };
}

async function createAdminReview(payload: {
  packageKey: string;
  name: string;
  rating: number;
  message: string;
  approved?: boolean;
}) {
  const res = await adminFetch("/api/admin/numeroloji/reviews", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Kaydedilemedi.");
  }

  return json as { ok: true; item: Review };
}

async function importFallbackReviews() {
  const res = await adminFetch("/api/admin/numeroloji/reviews/import-fallback", {
    method: "POST",
  });

  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "İçe aktarılamadı.");
  }

  return json as { ok: true; imported: number; skipped: number };
}

function formatDate(seconds: number) {
  try {
    return new Intl.DateTimeFormat("tr-TR", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(seconds * 1000));
  } catch {
    return "";
  }
}

export default function AdminNumerolojiReviews() {
  const queryClient = useQueryClient();
  const { sessionQuery, loginMutation, logoutMutation } = useAdminSession();
  const configured = Boolean(sessionQuery.data?.configured);
  const authenticated = Boolean(sessionQuery.data?.authenticated);

  const { theme, setTheme, systemTheme } = useTheme();
  const resolvedTheme = theme === "system" ? systemTheme : theme;
  const isDark = resolvedTheme === "dark";

  const [approvedTab, setApprovedTab] = useState<"pending" | "approved" | "all">("all");
  const [packageKeyFilter, setPackageKeyFilter] = useState("");

  const [newPackageKey, setNewPackageKey] = useState("premium");
  const [newName, setNewName] = useState("");
  const [newRating, setNewRating] = useState(5);
  const [newMessage, setNewMessage] = useState("");
  const [newApproved, setNewApproved] = useState(true);

  const storageQuery = useQuery({
    queryKey: ["admin-numeroloji-storage"],
    queryFn: fetchStorage,
    enabled: configured && authenticated,
    staleTime: 30_000,
  });

  const pendingCountQuery = useQuery({
    queryKey: ["admin-numeroloji-reviews-count", "pending", packageKeyFilter],
    queryFn: () =>
      fetchReviews({
        approved: false,
        packageKey: packageKeyFilter.trim() || undefined,
      }),
    enabled: configured && authenticated,
    staleTime: 10_000,
  });

  const approvedCountQuery = useQuery({
    queryKey: ["admin-numeroloji-reviews-count", "approved", packageKeyFilter],
    queryFn: () =>
      fetchReviews({
        approved: true,
        packageKey: packageKeyFilter.trim() || undefined,
      }),
    enabled: configured && authenticated,
    staleTime: 10_000,
  });

  const listQuery = useQuery({
    queryKey: ["admin-numeroloji-reviews", approvedTab, packageKeyFilter],
    queryFn: () =>
      fetchReviews({
        approved: approvedTab === "all" ? undefined : approvedTab === "approved",
        packageKey: packageKeyFilter.trim() || undefined,
      }),
    enabled: configured && authenticated,
    staleTime: 10_000,
  });

  const approveMutation = useMutation({
    mutationFn: setApproved,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin-numeroloji-reviews"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteReview,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin-numeroloji-reviews"] });
    },
  });

  const createMutation = useMutation({
    mutationFn: createAdminReview,
    onSuccess: async (_data, variables) => {
      // If user added as approved, it will show under the approved tab.
      if (variables?.approved) setApprovedTab("approved");
      else setApprovedTab("pending");

      setNewName("");
      setNewRating(5);
      setNewMessage("");
      setNewApproved(true);
      await queryClient.invalidateQueries({ queryKey: ["admin-numeroloji-reviews"] });
    },
  });

  const importFallbackMutation = useMutation({
    mutationFn: importFallbackReviews,
    onSuccess: async () => {
      setApprovedTab("approved");
      await queryClient.invalidateQueries({ queryKey: ["admin-numeroloji-reviews"] });
    },
  });

  const items = Array.isArray(listQuery.data?.items) ? listQuery.data.items : [];

  const grouped = useMemo(() => {
    const map = new Map<string, Review[]>();
    for (const r of items) {
      const key = r.packageKey || "?";
      map.set(key, [...(map.get(key) ?? []), r]);
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [items]);

  if (!configured) {
    return (
      <div className="mx-auto max-w-2xl py-16">
        <Card className="p-6">
          <div className="flex items-start gap-3">
            <ShieldAlert className="mt-0.5 h-5 w-5 text-amber-500" />
            <div>
              <div className="text-base font-semibold">Admin girişi ayarlı değil</div>
              <div className="mt-1 text-sm text-muted-foreground">
                Sunucu tarafında admin girişi ayarlanmalı.
              </div>
              <div className="mt-4">
                <Button asChild className="rounded-full">
                  <Link to="/admin/numeroloji">Admin panel</Link>
                </Button>
              </div>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  if (!authenticated) {
    return (
      <div className="mx-auto max-w-2xl py-16">
        <AdminLoginCard
          configured={configured}
          onLogin={(args) => loginMutation.mutate(args)}
          isLoading={loginMutation.isPending}
          error={loginMutation.isError ? (loginMutation.error as Error).message : undefined}
        />
      </div>
    );
  }

  return (
    <div className="flex min-h-dvh w-full">
      <NumerolojiAdminSidebar
        mode="links"
        isDark={isDark}
        onToggleTheme={() => setTheme(isDark ? "light" : "dark")}
      />

      <div className="flex min-w-0 flex-1 flex-col bg-slate-100/60 dark:bg-slate-900/30">
        <header className="flex h-16 items-center justify-between border-b border-slate-200/70 bg-white/70 px-4 backdrop-blur dark:border-slate-800/60 dark:bg-slate-950/40 sm:px-6">
          <div className="text-base font-semibold tracking-tight text-slate-900 dark:text-slate-50 sm:text-lg">Yorum Onayı</div>
          <Button
            variant="outline"
            className="rounded-full"
            size="sm"
            onClick={() => logoutMutation.mutate()}
            disabled={logoutMutation.isPending}
          >
            Çıkış
          </Button>
        </header>

        <div className="min-w-0 flex-1 p-4 sm:p-6">
          <div className="mx-auto w-full max-w-6xl">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <div className="text-lg font-semibold tracking-tight text-slate-900 dark:text-slate-50">Yorum onayları</div>
              <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Kullanıcı yorumları burada onaya düşer. Onaylananlar ürün sayfasında görünür.
              </div>

              {storageQuery.data?.reviews?.backend === "supabase" ? (
                <div className="mt-2 text-xs text-emerald-600 dark:text-emerald-400">Kalıcı kayıt: Supabase veritabanı</div>
              ) : storageQuery.data?.reviews?.backend === "disk" ? (
                <div className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                  Kalıcı kayıt: Sunucu dosyası (Supabase bağlı değil)
                </div>
              ) : null}
            </div>

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <Button
                variant={approvedTab === "pending" ? "default" : "outline"}
                className="rounded-full"
                onClick={() => setApprovedTab("pending")}
              >
                Bekleyen ({pendingCountQuery.data?.items?.length ?? 0})
              </Button>
              <Button
                variant={approvedTab === "approved" ? "default" : "outline"}
                className="rounded-full"
                onClick={() => setApprovedTab("approved")}
              >
                Onaylı ({approvedCountQuery.data?.items?.length ?? 0})
              </Button>
              <Button
                variant={approvedTab === "all" ? "default" : "outline"}
                className="rounded-full"
                onClick={() => setApprovedTab("all")}
              >
                Tümü ({(pendingCountQuery.data?.items?.length ?? 0) + (approvedCountQuery.data?.items?.length ?? 0)})
              </Button>
            </div>
          </div>

          <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-400">
              Filtre
            </div>
            <div className="flex items-center gap-2">
              <Input
                value={packageKeyFilter}
                onChange={(e) => setPackageKeyFilter(e.target.value)}
                placeholder="Paket anahtarı (örn: pin)"
                className="h-10 w-56"
              />
              <Button
                variant="outline"
                className="h-10 rounded-full"
                onClick={() => setPackageKeyFilter("")}
              >
                Temizle
              </Button>
            </div>
          </div>

          <div className="mt-6 rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-950/40">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="text-sm font-semibold tracking-tight text-slate-900 dark:text-slate-50">Manuel yorum ekle</div>
                <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  Eski sitedeki yorumları buradan ekleyebilirsin. Onaylı eklersen anında sitede görünür.
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  className="h-10 rounded-full"
                  onClick={() => importFallbackMutation.mutate()}
                  disabled={importFallbackMutation.isPending}
                >
                  Eski yorumları içe aktar
                </Button>
              </div>
            </div>

            <div className="mt-4 grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Paket anahtarı</Label>
                <Input
                  value={newPackageKey}
                  onChange={(e) => setNewPackageKey(e.target.value)}
                  placeholder="premium"
                  className="h-10"
                />
              </div>

              <div className="space-y-2">
                <Label>İsim Soyisim</Label>
                <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Ad Soyad" className="h-10" />
              </div>

              <div className="space-y-2">
                <Label>Yıldız (1-5)</Label>
                <Input
                  value={String(newRating)}
                  onChange={(e) => {
                    const n = Math.max(1, Math.min(5, Math.trunc(Number(e.target.value) || 0)));
                    setNewRating(n);
                  }}
                  className="h-10"
                />
              </div>

              <div className="flex items-end">
                <label className="flex h-10 w-full items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 text-sm dark:border-slate-800 dark:bg-slate-950">
                  <Checkbox checked={newApproved} onCheckedChange={(v) => setNewApproved(Boolean(v))} />
                  <span className="font-semibold text-slate-900 dark:text-slate-50">Onaylı ekle</span>
                </label>
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label>Yorum</Label>
                <Textarea value={newMessage} onChange={(e) => setNewMessage(e.target.value)} rows={4} placeholder="Yorum metni..." />
              </div>
            </div>

            {importFallbackMutation.isError ? (
              <div className="mt-3 rounded-xl border border-rose-500/30 bg-rose-500/10 p-3 text-sm text-rose-200">
                {(importFallbackMutation.error as Error).message}
              </div>
            ) : null}

            {createMutation.isError ? (
              <div className="mt-3 rounded-xl border border-rose-500/30 bg-rose-500/10 p-3 text-sm text-rose-200">
                {(createMutation.error as Error).message}
              </div>
            ) : null}

            <div className="mt-4 flex justify-end">
              <Button
                className="rounded-full"
                disabled={
                  createMutation.isPending ||
                  !newPackageKey.trim() ||
                  !newName.trim() ||
                  !newMessage.trim() ||
                  !Number.isFinite(newRating)
                }
                onClick={() =>
                  createMutation.mutate({
                    packageKey: newPackageKey.trim(),
                    name: newName.trim(),
                    rating: newRating,
                    message: newMessage.trim(),
                    approved: newApproved,
                  })
                }
              >
                Ekle
              </Button>
            </div>
          </div>

          {listQuery.isLoading ? (
            <div className="mt-8 rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-600 shadow-sm dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-300">
              Yorumlar yükleniyor...
            </div>
          ) : null}

          {listQuery.isError ? (
            <div className="mt-8 rounded-xl border border-rose-500/30 bg-rose-500/10 p-4 text-sm text-rose-200">
              {(listQuery.error as Error).message}
            </div>
          ) : null}

          {!listQuery.isLoading && !listQuery.isError ? (
            <div className="mt-8 grid gap-6">
              {!items.length ? (
                <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-600 shadow-sm dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-300">
                  Gösterilecek yorum yok.
                </div>
              ) : null}

              {grouped.map(([pkgKey, list]) => (
                <div key={pkgKey} className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-950/40">
                  <div className="mb-4 flex items-center justify-between">
                    <div className="text-sm font-semibold tracking-tight text-slate-900 dark:text-slate-50">
                      Paket: {pkgKey}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">{list.length} yorum</div>
                  </div>

                  <div className="grid gap-4">
                    {list.map((r) => (
                      <div key={r.id} className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/30">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                          <div>
                            <div className="text-sm font-semibold text-slate-900 dark:text-slate-50">{r.name}</div>
                            <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                              Puan: {r.rating}/5 • {formatDate(r.createdAt)}
                            </div>
                          </div>

                          <div className="flex flex-wrap gap-2">
                            {!r.approved ? (
                              <Button
                                className="h-9 rounded-full"
                                onClick={() => approveMutation.mutate({ id: r.id, approved: true })}
                                disabled={approveMutation.isPending}
                              >
                                <Check className="mr-2 h-4 w-4" />
                                Onayla
                              </Button>
                            ) : (
                              <Button
                                variant="outline"
                                className="h-9 rounded-full"
                                onClick={() => approveMutation.mutate({ id: r.id, approved: false })}
                                disabled={approveMutation.isPending}
                              >
                                <X className="mr-2 h-4 w-4" />
                                Yayından kaldır
                              </Button>
                            )}

                            <Button
                              variant="outline"
                              className={cn(
                                "h-9 rounded-full",
                                "border-rose-300 bg-rose-50 text-rose-700 hover:bg-rose-100",
                                "dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-200 dark:hover:bg-rose-500/20",
                              )}
                              onClick={() => deleteMutation.mutate(r.id)}
                              disabled={deleteMutation.isPending}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Sil
                            </Button>
                          </div>
                        </div>

                        <div className="mt-3 text-sm leading-relaxed text-slate-700 dark:text-slate-200">{r.message}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
